import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { AlertTriangle, Banknote, Settings2, FileText, Trash2, ChevronRight, Send, Check, Plus } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import {
  PAGE_WIDE, KpiTile, SectionCard, Dialog, Field, FormRow, FilterChips, EmptyState, ErrorState, TableSkeleton,
  StatusPill, money, plural, fmtDate,
} from '../../components/ui';
import { TABLE_WRAP, TABLE, THEAD_ROW, TH, TH_NUM, TD, TD_NUM, Notice, RefreshButton } from './financeKit';
import { codeWords } from '../../utils/format';

const warnIcon = <AlertTriangle size={16} className="text-amber-500" />;
const d10 = (d) => (d ? fmtDate(String(d).slice(0, 10)) : '—');

function downloadText(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Local YYYY-MM-DD.
const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
// The last completed Monday–Sunday fortnight: it ends on the most recent Sunday before today.
function lastFortnight() {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setDate(today.getDate() - (today.getDay() === 0 ? 7 : today.getDay()));
  const start = new Date(end); start.setDate(end.getDate() - 13);
  return { period_start: ymd(start), period_end: ymd(end) };
}

// Direct Entry (APCA) details the bank file needs before anyone can be paid.
const bankReady = (s) => !!(s && s.de_user_id && s.trace_bsb && s.trace_account);

// Org Direct Entry (APCA) bank details used to pay contractors — all UI fields, never hardcoded.
function PaymentSettings({ onClose, onSaved }) {
  const [f, setF] = useState({ de_user_id: '', bank_abbrev: '', remitter_name: '', trace_bsb: '', trace_account: '', lodgement_ref: '' });
  const [saving, setSaving] = useState(false);
  const [fallback, setFallback] = useState(false);
  useEffect(() => { api.get('/contractor-invoices/settings').then((r) => { setF((p) => ({ ...p, ...r.data })); setFallback(r.data?._fallback === 'pm'); }).catch(() => {}); }, []);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try { await api.put('/contractor-invoices/settings', f); toast.success('Payment settings saved'); onSaved && onSaved(); onClose && onClose(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <SectionCard title="Payment bank details (Direct Entry)" icon={Banknote} bodyClass="p-5 space-y-4"
      actions={onClose && <button type="button" onClick={onClose} className="btn-ghost btn-sm">Close</button>}>
      {fallback && <Notice tone="amber">Your Plan Management bank details are being used for now. Save here to set dedicated contractor-payment details.</Notice>}
      <FormRow cols={3}>
        <Field label="APCA / Direct Entry user ID" required>{(id) => <input id={id} className="input-field" value={f.de_user_id || ''} onChange={set('de_user_id')} placeholder="From your bank" />}</Field>
        <Field label="Remitter name">{(id) => <input id={id} className="input-field" value={f.remitter_name || ''} onChange={set('remitter_name')} placeholder="Your business name" />}</Field>
        <Field label="Bank abbreviation" hint="Three letters">{(id) => <input id={id} className="input-field" maxLength={3} value={f.bank_abbrev || ''} onChange={set('bank_abbrev')} placeholder="e.g. CBA" />}</Field>
        <Field label="Trace BSB" required>{(id) => <input id={id} className="input-field tabular-nums" value={f.trace_bsb || ''} onChange={set('trace_bsb')} placeholder="000-000" />}</Field>
        <Field label="Trace account" required>{(id) => <input id={id} className="input-field tabular-nums" value={f.trace_account || ''} onChange={set('trace_account')} placeholder="Account number" />}</Field>
        <Field label="Lodgement reference" hint="Optional">{(id) => <input id={id} className="input-field" value={f.lodgement_ref || ''} onChange={set('lodgement_ref')} />}</Field>
      </FormRow>
      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={save} disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save payment settings'}</button>
        <span className="text-xs text-slate-600">Your bank's Direct Entry (ABA) credentials, used to create the payment file. Required fields are needed before anyone can be paid.</span>
      </div>
    </SectionCard>
  );
}

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
];

export default function PayContractors() {
  const confirm = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [payCfg, setPayCfg] = useState(undefined);   // undefined = not loaded yet
  const [detail, setDetail] = useState(null);
  const [ticked, setTicked] = useState({});
  const [gen, setGen] = useState(() => ({ contractor_id: '', ...lastFortnight() }));
  const [genBusy, setGenBusy] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  const contractorRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const q = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/contractor-invoices${q}`).then((r) => setInvoices(r.data || []))
      .catch((e) => { setInvoices([]); setError(e.response?.data?.message || "We couldn't load contractor invoices. Check your connection, then try again."); })
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const loadPayCfg = useCallback(() => {
    api.get('/contractor-invoices/settings').then((r) => setPayCfg(r.data || {})).catch(() => setPayCfg(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/contractor-invoices/contractors').then((r) => setContractors(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { loadPayCfg(); }, [loadPayCfg]);

  // Scroll to and briefly mark the invoice that was just created.
  useEffect(() => {
    if (!justCreated || loading) return undefined;
    const el = document.getElementById(`ci-row-${justCreated}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setJustCreated(null), 4000);
    return () => clearTimeout(t);
  }, [justCreated, loading]);

  const genReady = !!(gen.contractor_id && gen.period_start && gen.period_end);

  const generate = async () => {
    if (!genReady) return toast.error('Pick a contractor and a period');
    setGenBusy(true);
    try {
      const r = await api.post('/contractor-invoices/generate-from-shifts', gen);
      toast.success(`Invoice ${r.data.invoice_number} created · ${r.data.line_count} shift${r.data.line_count === 1 ? '' : 's'}`);
      setGen((g) => ({ ...g, contractor_id: '' }));
      setJustCreated(r.data.id || null);
      load();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not generate'); } finally { setGenBusy(false); }
  };

  const openDetail = (id) => api.get(`/contractor-invoices/${id}`).then((r) => setDetail(r.data)).catch((e) => toast.error(e.response?.data?.message || 'Could not open invoice'));

  const setStatus = async (id, status) => {
    try { await api.patch(`/contractor-invoices/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); if (detail?.id === id) openDetail(id); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };
  const del = async (inv) => {
    const ok = await confirm('This cannot be undone. Its shifts can be invoiced again.', {
      title: `Delete invoice ${inv.invoice_number || ''} for ${inv.contractor_name || 'this contractor'}?`.replace('  ', ' '),
      confirmLabel: 'Delete invoice', danger: true,
    });
    if (!ok) return;
    try { await api.delete(`/contractor-invoices/${inv.id}`); toast.success('Deleted'); setDetail(null); load(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const payAba = async (ids, fallbackTotal) => {
    if (!ids.length) return;
    const rows = invoices.filter((v) => ids.includes(v.id));
    const total = rows.length ? rows.reduce((s, v) => s + Number(v.total || 0), 0) : Number(fallbackTotal || 0);
    const who = rows.length === 1 ? rows[0].contractor_name : plural(ids.length, 'contractor invoice');
    const ok = await confirm('Creates an ABA file for your bank to upload and emails a remittance to each contractor. Invoices are marked paid.', {
      title: `Pay ${who} ${money(total)}?`, confirmLabel: 'Create payment file',
    });
    if (!ok) return;
    try {
      const r = await api.post('/contractor-invoices/pay-aba', { invoice_ids: ids });
      if (r.data.aba) downloadText(r.data.filename || 'contractor-payment.aba', r.data.aba);
      toast.success(`Paid ${r.data.paid} · ${money(r.data.total)} · ${r.data.remitted} remittance email${r.data.remitted === 1 ? '' : 's'}`);
      if (r.data.missing?.length) toast(`Skipped (no bank details): ${r.data.missing.join(', ')}`, { icon: warnIcon, duration: 8000 });
      setTicked({}); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Payment failed'); }
  };

  const approvedIds = invoices.filter((v) => v.status === 'approved' && ticked[v.id]).map((v) => v.id);
  const selectedTotal = invoices.filter((v) => ticked[v.id] && v.status === 'approved').reduce((s, v) => s + Number(v.total || 0), 0);

  const approvedTotal = invoices.filter((v) => v.status === 'approved').reduce((s, v) => s + Number(v.total || 0), 0);
  const approvedCount = invoices.filter((v) => v.status === 'approved').length;
  const pendingCount = invoices.filter((v) => v.status === 'draft' || v.status === 'submitted').length;
  const paidTotal = invoices.filter((v) => v.status === 'paid').reduce((s, v) => s + Number(v.total || 0), 0);
  const paidCount = invoices.filter((v) => v.status === 'paid').length;
  const allZero = !loading && invoices.length === 0;
  const payIncomplete = payCfg !== undefined && payCfg !== null && !bankReady(payCfg);

  return (
    <div className={PAGE_WIDE}>
      {/* Actions — the top bar already names the page */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600 max-w-2xl">Turn a contractor's approved shifts into an invoice, then pay it by bank file with an emailed remittance.</p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowSettings((s) => !s)} aria-expanded={showSettings} className="btn-secondary"><Settings2 size={15} /> Payment settings</button>
          <RefreshButton onClick={load} loading={loading} label="Refresh invoices" />
        </div>
      </div>

      {payIncomplete && !showSettings && (
        <Notice tone="amber" title="Bank file details are not set up"
          actions={<button type="button" onClick={() => setShowSettings(true)} className="btn-secondary btn-sm">Set up bank file</button>}>
          Contractors can't be paid until your Direct Entry user ID, trace BSB and trace account are saved.
        </Notice>
      )}

      {showSettings && <PaymentSettings onClose={() => setShowSettings(false)} onSaved={loadPayCfg} />}

      {/* Money-first summary — one caption line when there is nothing yet */}
      {allZero ? (
        !error && <p className="text-xs text-slate-500">No contractor invoices yet · nothing to pay.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiTile label="To pay (approved)" accent="primary" value={loading ? '…' : money(approvedTotal)} sub={loading ? null : plural(approvedCount, 'approved invoice')} />
          <KpiTile label="Pending review" accent="amber" value={loading ? '…' : pendingCount} sub="Draft and submitted" />
          <KpiTile label="Paid" accent="emerald" value={loading ? '…' : money(paidTotal)} sub={loading ? null : plural(paidCount, 'invoice')} />
          <KpiTile label="Invoices" accent="slate" value={loading ? '…' : invoices.length} sub="In this view" />
        </div>
      )}

      {/* Generate from shifts */}
      <SectionCard title="Generate an invoice from approved shifts" icon={Plus} bodyClass="p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <Field label="Contractor">
            {(id) => (
              <select id={id} ref={contractorRef} className="input-field" value={gen.contractor_id} onChange={(e) => setGen((g) => ({ ...g, contractor_id: e.target.value }))}>
                <option value="">Select…</option>
                {contractors.map((c) => <option key={c.id} value={c.id}>{c.name}{c.has_bank ? '' : ' (no bank details)'}</option>)}
              </select>
            )}
          </Field>
          <Field label="Period start">{(id) => <input id={id} type="date" className="input-field" value={gen.period_start} max={gen.period_end || undefined} onChange={(e) => setGen((g) => ({ ...g, period_start: e.target.value }))} />}</Field>
          <Field label="Period end">{(id) => <input id={id} type="date" className="input-field" value={gen.period_end} min={gen.period_start || undefined} onChange={(e) => setGen((g) => ({ ...g, period_end: e.target.value }))} />}</Field>
          <button type="button" onClick={generate} disabled={genBusy || !genReady} className="btn-primary">{genBusy ? 'Generating…' : 'Generate invoice'}</button>
        </div>
        <p className="text-xs text-slate-600 mt-2">
          {!genReady ? 'Choose a contractor and a period. ' : ''}
          Pulls the contractor's <strong>approved</strong> shifts in the period that aren't invoiced yet, priced at their contractor pay rate. If the contractor is marked as registered for GST on their staff profile, 10% GST is added to the services (not to km or expenses). You can change the GST on the invoice.
        </p>
      </SectionCard>

      {/* Invoice list */}
      <SectionCard
        title={`Contractor invoices${loading ? '' : ` · ${invoices.length}`}`}
        icon={FileText}
        actions={(
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <FilterChips ariaLabel="Invoice status" multi={false} options={STATUS_FILTERS} value={statusFilter || null} onChange={(v) => setStatusFilter(v || '')} />
            {approvedIds.length > 0 && (
              <button type="button" onClick={() => payAba(approvedIds)} className="btn-primary btn-sm">
                <Banknote size={14} /> Pay {approvedIds.length} selected · {money(selectedTotal)}
              </button>
            )}
          </div>
        )}
      >
        {error ? <ErrorState message={error} onRetry={load} />
          : loading ? <TableSkeleton rows={5} cols={6} />
          : invoices.length === 0 ? (
            <EmptyState icon={FileText}
              title={statusFilter ? 'No invoices with this status' : 'No contractor invoices yet'}
              hint={statusFilter ? 'Pick another status, or All.' : "Generate one from a contractor's approved shifts above."}
              action={!statusFilter && <button type="button" onClick={() => contractorRef.current && contractorRef.current.focus()} className="btn-secondary">Generate from approved shifts</button>} />
          ) : (
            <div className={TABLE_WRAP}>
              <table className={TABLE}>
                <thead>
                  <tr className={THEAD_ROW}>
                    <th scope="col" className={`${TH} w-10`}><span className="sr-only">Select to pay</span></th>
                    <th scope="col" className={TH}>Contractor</th>
                    <th scope="col" className={TH}>Invoice</th>
                    <th scope="col" className={TH}>Period</th>
                    <th scope="col" className={TH_NUM}>Lines</th>
                    <th scope="col" className={TH_NUM}>Total</th>
                    <th scope="col" className={TH}>Status</th>
                    <th scope="col" className={TH_NUM}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((v) => (
                    <tr key={v.id} id={`ci-row-${v.id}`}
                      className={clsx('border-b border-slate-100 last:border-0 transition-colors duration-200', justCreated === v.id ? 'bg-primary-50' : 'hover:bg-slate-50/60')}>
                      <td className={TD}>{v.status === 'approved' && (
                        <input type="checkbox" className="w-4 h-4 rounded" checked={!!ticked[v.id]} onChange={(e) => setTicked((t) => ({ ...t, [v.id]: e.target.checked }))}
                          aria-label={`Select ${v.contractor_name} ${v.invoice_number} to pay`} />
                      )}</td>
                      <td className={TD}>
                        <span className="font-medium text-slate-800">{v.contractor_name}</span>
                        {v.contractor_abn ? <span className="block text-xs text-slate-500">ABN {v.contractor_abn}</span> : null}
                      </td>
                      <td className={`${TD} font-mono text-xs text-slate-700 whitespace-nowrap`}>{v.invoice_number}</td>
                      <td className={`${TD} text-xs text-slate-600 whitespace-nowrap`}>{d10(v.period_start)}{v.period_end ? ` – ${d10(v.period_end)}` : ''}</td>
                      <td className={TD_NUM}>{v.line_count}</td>
                      <td className={`${TD_NUM} font-semibold`}>{money(v.total)}</td>
                      <td className={TD}><StatusPill status={v.status || 'draft'} /></td>
                      <td className={`${TD} text-right`}>
                        <button type="button" onClick={() => openDetail(v.id)} aria-label={`Open invoice ${v.invoice_number}`} className="btn-ghost btn-sm">Open <ChevronRight size={13} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </SectionCard>

      {detail && <DetailModal inv={detail} onClose={() => setDetail(null)} onStatus={setStatus} onPay={payAba} onDelete={del} onReload={() => openDetail(detail.id)} />}
    </div>
  );
}

function DetailModal({ inv, onClose, onStatus, onPay, onDelete, onReload }) {
  const [gst, setGst] = useState(String(inv.gst ?? 0));
  const [savingGst, setSavingGst] = useState(false);
  const saveGst = async () => {
    setSavingGst(true);
    try {
      const { data } = await api.patch(`/contractor-invoices/${inv.id}`, { gst: parseFloat(gst) || 0 });
      toast.success(`GST updated · total ${money(data?.total ?? (Number(inv.subtotal || 0) + (parseFloat(gst) || 0)))}`);
      onReload();
    }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSavingGst(false); }
  };
  const [invNo, setInvNo] = useState(inv.invoice_number || '');
  const [notes, setNotes] = useState(inv.notes || '');
  const [savingMeta, setSavingMeta] = useState(false);
  const metaDirty = invNo !== (inv.invoice_number || '') || notes !== (inv.notes || '');
  const saveMeta = async () => {
    setSavingMeta(true);
    try { await api.patch(`/contractor-invoices/${inv.id}`, { invoice_number: invNo, notes }); toast.success('Invoice details updated'); onReload(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSavingMeta(false); }
  };
  const editable = inv.status !== 'paid';
  // The server accepts GST changes only while the invoice is being prepared.
  const gstEditable = ['draft', 'submitted'].includes(inv.status);
  const gstNum = parseFloat(gst);
  const gstChanged = String(gst) !== String(inv.gst ?? 0);
  const previewTotal = Number(inv.subtotal || 0) + (Number.isFinite(gstNum) ? gstNum : 0);

  const footer = (
    <div className="flex w-full items-center justify-between gap-2 flex-wrap">
      <div>{['draft', 'submitted', 'rejected'].includes(inv.status) && (
        <button type="button" onClick={() => onDelete(inv)} className="btn-ghost text-rose-700 hover:!bg-rose-50"><Trash2 size={14} /> Delete</button>
      )}</div>
      <div className="flex items-center gap-2 flex-wrap">
        {['draft', 'submitted'].includes(inv.status) && <>
          <button type="button" onClick={() => onStatus(inv.id, 'rejected')} className="btn-secondary">Reject</button>
          <button type="button" onClick={() => onStatus(inv.id, 'approved')} className="btn-primary"><Check size={15} /> Approve</button>
        </>}
        {inv.status === 'approved' && <>
          <button type="button" onClick={() => onStatus(inv.id, 'draft')} className="btn-secondary">Un-approve</button>
          <button type="button" onClick={() => onPay([inv.id], inv.total)} disabled={inv.bank && !inv.bank.has_bank} className="btn-primary"><Banknote size={15} /> Pay by ABA</button>
        </>}
        {inv.status === 'paid' && <span className="text-sm text-emerald-800 flex items-center gap-1.5"><Send size={14} /> Paid{inv.remittance_sent_at ? ' · remittance sent' : ''}</span>}
      </div>
    </div>
  );

  return (
    <Dialog open onClose={onClose} size="lg"
      title={`${inv.contractor_name} · ${inv.invoice_number}`}
      description={`${d10(inv.period_start)}${inv.period_end ? ` – ${d10(inv.period_end)}` : ''}${inv.paid_at ? ` · paid ${d10(inv.paid_at)}` : ''}`}
      footer={footer}>
      <div className="space-y-4">
        <div><StatusPill status={inv.status || 'draft'} /></div>
        {inv.bank && !inv.bank.has_bank && (
          <Notice tone="amber">No bank details on this contractor's profile. Add a BSB and account before paying.</Notice>
        )}
        <div className={TABLE_WRAP}>
          <table className={TABLE}>
            <thead><tr className={THEAD_ROW}>
              <th scope="col" className={TH}>Date</th><th scope="col" className={TH}>Description</th>
              <th scope="col" className={TH_NUM}>Hours</th><th scope="col" className={TH_NUM}>Rate</th><th scope="col" className={TH_NUM}>Amount</th>
            </tr></thead>
            <tbody>
              {(inv.lines || []).map((l) => (
                <tr key={l.id} className="border-b border-slate-100 last:border-0">
                  <td className={`${TD} text-xs text-slate-600 whitespace-nowrap`}>{d10(l.work_date)}</td>
                  <td className={TD}>{codeWords(l.description)}</td>
                  <td className={TD_NUM}>{Number(l.hours).toFixed(2)}</td>
                  <td className={TD_NUM}>{money(l.rate)}</td>
                  <td className={`${TD_NUM} font-medium`}>{money(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col items-end gap-2 text-sm">
          <div className="flex gap-8"><span className="text-slate-600">Subtotal</span><span className="tabular-nums w-28 text-right">{money(inv.subtotal)}</span></div>
          {gstEditable ? (
            <div className="w-full sm:w-auto">
              <Field label="GST" hint={gstChanged && Number.isFinite(gstNum) ? `New total ${money(previewTotal)}` : 'Up to 10% of the subtotal.'}>
                {(id) => (
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" aria-hidden="true">$</span>
                      <input id={id} type="number" step="0.01" min="0" value={gst} onChange={(e) => setGst(e.target.value)}
                        className="input-field !w-32 !pl-7 text-right tabular-nums" />
                    </div>
                    <button type="button" onClick={saveGst} disabled={savingGst || !gstChanged} className="btn-secondary">{savingGst ? 'Saving…' : 'Update GST'}</button>
                  </div>
                )}
              </Field>
            </div>
          ) : (
            <div className="flex gap-8"><span className="text-slate-600">GST</span><span className="tabular-nums w-28 text-right">{money(inv.gst)}</span></div>
          )}
          {!gstEditable && inv.status === 'approved' && <p className="text-xs text-slate-500">Un-approve it to change the GST.</p>}
          <div className="flex gap-8 font-bold text-slate-900 border-t border-slate-200 pt-1 mt-1"><span>Total</span><span className="tabular-nums w-28 text-right">{money(inv.total)}</span></div>
        </div>

        {editable && (
          <div className="pt-4 border-t border-slate-100 space-y-3">
            <FormRow cols={2}>
              <Field label="Invoice number">
                {(id) => <input id={id} value={invNo} onChange={(e) => setInvNo(e.target.value)} placeholder={inv.invoice_number || ''} className="input-field font-mono" />}
              </Field>
              <Field label="Notes">
                {(id) => <textarea id={id} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Internal notes for this invoice…" className="input-field resize-y" />}
              </Field>
            </FormRow>
            <div className="flex justify-end">
              <button type="button" onClick={saveMeta} disabled={savingMeta || !metaDirty} className="btn-secondary btn-sm">
                {savingMeta ? 'Saving…' : 'Save details'}
              </button>
            </div>
          </div>
        )}
        {!editable && inv.notes && (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-xs font-medium text-slate-600 mb-1">Notes</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{inv.notes}</p>
          </div>
        )}
      </div>
    </Dialog>
  );
}
